/**
 * ============================================================================
 * OWNER-ADOPTED, NOT COUNSEL-REVIEWED. DO NOT TREAT AS LEGALLY SUFFICIENT.
 * ============================================================================
 *
 * Every string in this module was written from what the system demonstrably
 * does: `docs/student-data-inventory.md`, the migrations and RLS policies it
 * records, `docs/PRD.md` §10, and the transport code in `src/components/apply`,
 * `src/lib/google-drive-picker.ts`, and the mail paths the inventory names.
 *
 * On 2026-08-13 the owner answered the policy questions this module had left
 * open, recorded in `docs/archive/design-275/275-owner-decisions.md` groups C, D and E.
 * Those answers are adopted here. They are OWNER DECISIONS, not counsel
 * opinions: no qualified legal reviewer has confirmed them, and the standing
 * instruction on every one of them is that counsel confirms or overrides.
 *
 * On 2026-11-04 the owner ruled the Drive chooser change (D1, D9: access to
 * picked files only, plus upload into the course folder) material, so the
 * effective date moved to that release. The policy keeps no change record
 * of its own; the git history of this file is the record.
 *
 * Issue #65 ("student-data inventory, provider disclosures, retention, access,
 * deletion, and incident obligations") remains OPEN. What survives of it is
 * narrower than it was: the registered address and named data controller for
 * each entity (D1), and execution of the three provider agreements (D5).
 *
 * Facts nobody has supplied yet are marked with a `pending()` block that renders
 * the literal text `[NEEDS OWNER INPUT: …]` on the page. Do not silently delete
 * one; either the owner answers it or it stays visible.
 *
 * Tenant differences are derived from the explicit admissions contract that
 * also decides whether `ApplyPage` renders the form or redirects. The landing
 * page presentation type is not an admissions ownership signal.
 */
import type { LandingContent } from "@/content/landing";

/**
 * Where the two public legal documents are served. Declared here, beside the
 * documents themselves, so the routes, the pages, and every link that points at
 * them share one source.
 */
export const LEGAL_EFFECTIVE_DATE = "2026-11-04";

/**
 * ONE date across both documents and both schools, per decision D3. It is the
 * date the owner adopted these answers, which is when the commitments below
 * became real; it is deliberately not the date the earlier draft first appeared.
 *
 * Move it ONLY on a material change. Under decision E3 the school notifies a
 * material change by this date and the change log alone, with no email and no
 * in-app notice, so this constant is the entire notification mechanism.
 */
export const LEGAL_ROUTES = {
  privacy: "/privacy",
  terms: "/terms",
} as const;

/** One rendered element of a legal section. */
export type LegalBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "definitions"; items: { term: string; description: string }[] }
  | { kind: "pending"; text: string };

export interface LegalSection {
  /** Stable anchor so a reviewer can cite one clause. */
  id: string;
  heading: string;
  blocks: LegalBlock[];
}

export interface LegalDocument {
  title: string;
  /** One-line statement of what the document covers. */
  lede: string;
  sections: LegalSection[];
}

/** The facts a legal document needs about the school serving this host. */
export interface LegalTenantFacts {
  /** The school as the portal presents it, from server branding or the host registry. */
  institutionName: string;
  /** Where a person writes about their own data; null when the portal holds none. */
  contactEmail: string | null;
  /** The parent that is the responsible legal entity, or null when the school is its own. */
  operatorEntityName: string | null;
  /** True when the public application form on THIS host writes an applicant record. */
  collectsApplicationsHere: boolean;
  /** Where admissions runs when it does not run on this host. */
  externalAdmissionsUrl: string | null;
}

/**
 * Derive the legal facts from the same content the public surfaces already use.
 *
 * `content.admissions.kind` is the discriminant `ApplyPage` itself branches on.
 * Reusing it means the disclosure and the behaviour cannot disagree even when
 * a program tenant, such as Mufid, runs admissions inside the portal.
 *
 * `contactEmail` is deliberately NOT taken from that discriminant. Admissions
 * behaviour has nothing to do with whether a school can be written to, and
 * reading it from the academy branch alone is what left Mufid with no contact
 * address on a published privacy policy.
 */
export function legalTenantFacts(
  institutionName: string,
  content: LandingContent,
): LegalTenantFacts {
  const portalAdmissions =
    content.admissions.kind === "portal" ? content.admissions : null;
  return {
    institutionName,
    contactEmail: content.footer.email ?? null,
    operatorEntityName: content.operatorEntityName,
    collectsApplicationsHere: portalAdmissions !== null,
    externalAdmissionsUrl:
      content.admissions.kind === "external" ? content.admissions.url : null,
  };
}

/** The banner every legal surface renders above the document body. */
export const LEGAL_DRAFT_NOTICE = {
  label: "Adopted by the school - pending legal review",
  body:
    "This text was prepared from the portal's own record of what it stores and where that data travels, and the school has adopted the commitments in it. It has not been reviewed by a qualified legal adviser, so it may yet change. Passages marked [NEEDS OWNER INPUT] are disclosures the school still has to supply.",
} as const;

function pending(question: string): LegalBlock {
  return { kind: "pending", text: `[NEEDS OWNER INPUT: ${question}]` };
}

function paragraph(text: string): LegalBlock {
  return { kind: "paragraph", text };
}

function list(...items: string[]): LegalBlock {
  return { kind: "list", items };
}

function contactBlocks(facts: LegalTenantFacts): LegalBlock[] {
  return facts.contactEmail
    ? [paragraph(`Write to ${facts.contactEmail}.`)]
    : [
        pending(
          `a contact address for privacy and records questions at ${facts.institutionName}; the portal has none configured, so this page currently gives a reader nowhere to write`,
        ),
      ];
}

const OPEN_OBLIGATIONS_BLOCK = paragraph(
  "The school has settled how long each record is kept, which rules it holds itself to, and what it commits to on a security incident. Two things remain outstanding: the registered address and named data controller of the responsible entity, and signed agreements with the service providers named below. Both are marked where they belong below.",
);

export function privacyPolicyDocument(facts: LegalTenantFacts): LegalDocument {
  const { institutionName } = facts;

  return {
    title: "Privacy Policy",
    lede: `How ${institutionName} collects, uses, and protects personal information in its academic portal.`,
    sections: [
      {
        id: "status",
        heading: "Status of this document",
        blocks: [
          paragraph(
            "This policy describes what the portal actually does today, verified against the system's own data inventory, database rules, and application code rather than assembled from boilerplate. The school has adopted it. It has not yet been reviewed by a qualified legal adviser, and that review may change it.",
          ),
          OPEN_OBLIGATIONS_BLOCK,
          paragraph(
            `This policy takes effect on ${LEGAL_EFFECTIVE_DATE} and applies from that date to everyone whose records the portal holds.`,
          ),
        ],
      },
      {
        id: "who-is-responsible",
        heading: "Who is responsible for this information",
        blocks: [
          paragraph(
            `${institutionName} operates this portal and decides what is recorded in it.`,
          ),
          paragraph(
            facts.operatorEntityName
              ? `${institutionName} is operated by ${facts.operatorEntityName}, which is the responsible legal entity for these records and the party this policy is with.`
              : `${institutionName} is its own legal entity and is the party this policy is with. It is not operated by, and does not share records with, any other school using this software.`,
          ),
          pending(
            `the registered postal address and the named data controller for the entity responsible for ${institutionName}`,
          ),
          paragraph(
            "No supervisory authority or education regulator has been identified as having jurisdiction over these records: the school is a United States religious institution, it receives no federal education funding, and it has no establishment in the European Union or the United Kingdom. A complaint is escalated to the named data controller above and then to IMAM's leadership. The school will revisit this if it enrolls a student in a jurisdiction that changes the answer.",
          ),
          ...contactBlocks(facts),
        ],
      },
      {
        id: "scope",
        heading: "What this portal is",
        blocks: [
          paragraph(
            "The portal is the school's academic system of record. It holds admissions applications, student and instructor records, programs, terms, courses, enrollment, attendance, working grades, course material, and announcements.",
          ),
          paragraph(
            `Each school using this software is a separate tenant with its own records. Every record carries the school it belongs to, the database enforces that boundary on every read and write in addition to the reader's role, and access fails closed when the school and role cannot both be established. No role at one school can read another school's records. This page describes only ${institutionName}.`,
          ),
          facts.collectsApplicationsHere
            ? paragraph(
                "The public application form on this site is part of the portal, and this policy covers it.",
              )
            : paragraph(
                `Admissions for ${institutionName} is not run on this site. The admissions link leads to the school's own website${
                  facts.externalAdmissionsUrl ? ` (${facts.externalAdmissionsUrl})` : ""
                }, which has its own policy; this policy does not cover it. No application is submitted through this portal.`,
              ),
        ],
      },
      {
        id: "what-we-collect",
        heading: "What the portal collects",
        blocks: [
          ...(facts.collectsApplicationsHere
            ? [
                paragraph(
                  "From an applicant, through the public application form: the applicant's name, email address, phone number, whether the person applying is the prospective student or a guardian, and any message they choose to write. The form asks each applicant not to include medical, financial, or government identity documents, and the portal has no field that stores them.",
                ),
                paragraph(
                  "Where the school has enabled the additional intake fields, the same form also collects the prospective student's date of birth, school name, grade level, and area of study, together with the name, phone number, and address of each named guardian. These fields are collected only where the school has switched them on. A school that has not switched a field on never holds it. Guardian details are held about a person who did not fill in the form themselves.",
                ),
                paragraph(
                  "The portal also derives a submission identifier and fingerprint for each application so that a retry or a double-click does not create a duplicate record. These are operational values, not information about the applicant.",
                ),
                paragraph(
                  "The application form is protected by Cloudflare Turnstile, a challenge widget served by Cloudflare. Cloudflare receives the request data needed to decide whether the submission is automated.",
                ),
              ]
            : []),
          paragraph(
            "For an account: an email address, a password, and a display name. The password is held only as a hash by the school's dedicated authentication service and is never returned by any part of the portal.",
          ),
          paragraph(
            "For a student record: the student identifier the institution issues, legal name, preferred name, initials and form of address, email address, phone number, postal address, country and city, an emergency contact, program, cohort, and enrollment status. Where the school's own admissions policy requires them, the record can also hold gender and a note about citizenship.",
          ),
          paragraph(
            "For an academic record: enrollment and withdrawal, attendance for each session, working grades for individual assessment items, and a projected course result. For an online session the instructor can also record whether the student's camera was on; that is kept as an observation and is not an input to either attendance percentage the portal calculates.",
          ),
          paragraph(
            "For an instructor: academic credentials shown to learners - form of address, initials, title, specialization, and training chain - and an institutional email address.",
          ),
          paragraph(
            "For communication: announcements published by the school or by a course instructor, which are staff-written text and routinely name people; a per-recipient marker of what has been read; and notifications.",
          ),
          paragraph(
            "Internal notes: the admissions team and administrators can record working notes about an applicant or a student. These are held in tables that no student or instructor route can read.",
          ),
          paragraph(
            "Course material and session recordings: instructors publish material to their course and can record where a session recording is stored. A student is given a recording link only for a session their own attendance record marks as excused.",
          ),
        ],
      },
      {
        id: "minors",
        heading: "Participants who are minors",
        blocks: [
          paragraph(
            "The portal is capable of holding a minor's date of birth and the names, phone numbers, and home address of that minor's guardians, where the school has enabled those fields on its application form. Those values are readable only by school administrators; they are never returned to a student or instructor, and they are not copied onto the student record when an applicant is admitted.",
          ),
          paragraph(
            "Recording a guardian's contact details as part of an application does not by itself give that guardian an account or standing access to the student's records after admission. The portal grants none today.",
          ),
          paragraph(
            "The school does not receive federal education funding, so FERPA does not apply to it as a statute. It nonetheless holds itself to a strict baseline: guardian consent to the standard COPPA requirement for any participant under 13, and FERPA-equivalent handling of every academic record. The school commits to this as its own policy, not as a claim about which statutes bind it.",
          ),
          paragraph(
            "Where the school has enabled the date-of-birth field and an applicant is under 13, the application form requires a named guardian and that guardian's explicit consent before the application can be submitted, and the consent is recorded against the application with the date it was given. A guardian withdraws consent by writing to the address below. Withdrawal deactivates the account, because the school cannot lawfully continue to hold the minor's data for it without consent.",
          ),
        ],
      },
      {
        id: "how-it-is-used",
        heading: "How the school uses this information",
        blocks: [
          list(
            "To review an application, contact the applicant or their guardian about it, and place an admitted student in the right program and cohort.",
            "To run courses: enrollment, rosters, attendance, assessment, and results.",
            "To reach a person about their own studies, including announcements a school or instructor publishes.",
            "To keep an academic history that stays provable for alumni.",
            "To operate and secure the portal itself.",
          ),
          paragraph(
            "The portal does not use this information for advertising, and it does not sell or rent it.",
          ),
        ],
      },
      {
        id: "who-can-see-it",
        heading: "Who can see what",
        blocks: [
          {
            kind: "definitions",
            items: [
              {
                term: "Students",
                description:
                  "See their own record and their own results, attendance, assignments, and announcements. A student can change only their preferred name, phone number, and postal address; every other field on their record is administrator-written, and the database rejects a change to any other field even if a request tries.",
              },
              {
                term: "Instructors",
                description:
                  "See the roster of the courses they teach and the attendance, working grades, and results of the students enrolled in them. Instructors do not have directory access to student records, and they never see admissions applications, guardian details, or internal notes.",
              },
              {
                term: "Administrators",
                description:
                  "See the school's records, including applications, guardian details, and internal notes. Administrative changes to a record record who made them and when.",
              },
            ],
          },
          paragraph(
            "Some information is deliberately visible to everyone signed in at the school: an instructor's published academic credentials and institutional email address, the courses each instructor teaches, and who leads a department.",
          ),
        ],
      },
      {
        id: "service-providers",
        heading: "Service providers",
        blocks: [
          paragraph(
            "The portal runs on infrastructure operated by other companies, and a small amount of information reaches them in the course of running it.",
          ),
          {
            kind: "definitions",
            items: [
              {
                term: "Google Cloud",
                description:
                  "Hosts the application server, the database, the private storage used for submitted files, and the application logs.",
              },
              {
                term: "Cloudflare",
                description:
                  "Serves this website and, on the application form, the Turnstile challenge widget.",
              },
              {
                term: "Resend",
                description:
                  "Delivers the portal's email: application confirmations, account invitations, password-recovery links, and announcement emails. It receives the recipient's address and the content of the message.",
              },
              {
                term: "Google (Drive file chooser)",
                description:
                  "Only when a signed-in instructor or administrator chooses to attach a Google Drive file to a course. The chooser asks Google for access only to the files that person picks, or uploads through it into the course's folder. The resulting access token stays in that person's browser: it is never sent to the school's servers, never written to storage, and never logged. What the portal keeps is the chosen file's link, name, type, and size.",
              },
            ],
          },
          paragraph(
            "IMAM owns coursework, submissions, feedback, grades, and certified outcomes. Google Drive may hold course materials and Google Meet may be a meeting destination, but neither receives academic records as a coursework platform.",
          ),
          paragraph(
            "The school has resolved to enter each provider's standard data-processing agreement, covering that provider's retention rule, its deletion route, and the path by which it notifies the school of an incident.",
          ),
          pending(
            "confirmation that the data-processing agreement with each provider named above has actually been executed; the school has decided which agreements it will enter, but none is signed yet, and this is required before public launch",
          ),
        ],
      },
      {
        id: "retention",
        heading: "How long records are kept",
        blocks: [
          {
            kind: "definitions",
            items: [
              {
                term: "Academic history",
                description:
                  "Kept indefinitely. Course outcomes, enrollment, and the student identity that binds them have to stay provable for alumni. A student identifier is issued once, never edited, and never reused.",
              },
              {
                term: "Contact details, attendance, working grades, and notes",
                description:
                  "Kept while the person is active, then for seven years after their last enrollment ends, then deleted.",
              },
              {
                term: "Applications",
                description:
                  "Kept for three years after the admissions decision where the applicant is not admitted. Where an applicant is admitted, the record follows the student rules instead.",
              },
              {
                term: "Read markers and notifications",
                description: "Kept for one year, then deleted.",
              },
              {
                term: "Session recordings",
                description:
                  "The school does not hold recordings in this portal. What it holds is a note of where a recording is stored, and it releases that link only to a student whose own attendance for that session is marked excused. How long the recording itself is kept is a rule the school will set when the portal begins holding recordings; it does not hold any today.",
              },
            ],
          },
          paragraph(
            "The school has confirmed these periods. Academic history is kept permanently and deliberately: a course outcome and the certification that follows it have to stay provable for an alumnus who asks decades later, so no retention period is applied to them. Seven years and three years apply to the operational records and applications named above, and to nothing else.",
          ),
          paragraph(
            "Application logs are kept for thirty days. Email addresses are being removed from log lines at the point they are written, so that a log cannot become a second, longer-lived copy of the contact details above; until that change is complete an address can still appear in an administrative log line and is deleted with the log at thirty days.",
          ),
          paragraph(
            "Deletion is not instant everywhere. Database backups are kept for fourteen days, so a deleted record can survive in a backup for up to that long.",
          ),
        ],
      },
      {
        id: "your-choices",
        heading: "Seeing, correcting, and deleting your information",
        blocks: [
          paragraph(
            "A student can see their own record in the portal and can change their preferred name, phone number, and postal address themselves. Any other correction is made by an administrator, on evidence, and the change records who made it.",
          ),
          paragraph(
            "An applicant, or a guardian named on an application, can ask the school to correct or remove their own details. Working notes written by staff are internal and are not part of what is returned on request unless the school is obliged to disclose them.",
          ),
          paragraph(
            "Some records cannot be deleted on request while they remain the school's academic history: a completed course outcome, the enrollment it belongs to, and the student identity that binds them. Where a person leaves, the school prefers to deactivate an account rather than delete it, so that history stays interpretable.",
          ),
          paragraph(
            "The school offers everyone whose data it holds the right to see it, correct it, receive a copy of it, and ask for it to be deleted, regardless of where that person lives. It does this as a matter of policy rather than because a particular statute compels it, so nobody has to establish which law covers them before asking.",
          ),
          paragraph(
            "Write to the address below. The school responds within thirty days. Before acting it confirms the request came from the person it concerns: the request has to come from the address held on the account, and the school asks for one further detail from the record that only that person would know. The limits in the paragraph above still apply, so a request to erase a completed course outcome will be refused with the reason given.",
          ),
        ],
      },
      {
        id: "security",
        heading: "How records are protected",
        blocks: [
          list(
            "Each school's records are isolated at the database itself, not only in application code. Every query must establish both the school and the reader's role, and it fails closed when either is missing.",
            "The portal has its own dedicated authentication service and its own database, separate from other systems.",
            "Submitted files are held in private storage whose authority is the database record that references them.",
            "Fields that must never reach a learner - internal notes, guardian details, an applicant's date of birth - are held so that no student or instructor route can return them.",
          ),
          paragraph(
            "If the school confirms a security incident that affected personal information it holds, it notifies every person whose data was affected within 72 hours of confirming it, by email to the address on their record, and tells them what was affected and what to do. This is a commitment the school makes on its own account. It is not a statutory notification, and as set out above no supervisory authority has been identified to file one with.",
          ),
        ],
      },
      {
        id: "cookies",
        heading: "Cookies, storage, and tracking",
        blocks: [
          paragraph(
            "The portal carries no advertising and no analytics or tracking scripts. Its typefaces are served from this site rather than from a content delivery network, so viewing a page does not report it to a third party.",
          ),
          paragraph(
            "When you sign in, your session and display name are stored by your own browser under a key belonging to this portal. Clearing it happens when you sign out. On a shared device the session stays until then.",
          ),
          facts.collectsApplicationsHere
            ? paragraph(
                "Two third-party scripts load, and only in the places named above: the Cloudflare Turnstile widget on the application form, and Google's file chooser when a signed-in staff member opens it.",
              )
            : paragraph(
                "One third-party script loads, and only in the place named above: Google's file chooser, when a signed-in staff member opens it.",
              ),
        ],
      },
      {
        id: "changes",
        heading: "Changes to this policy",
        blocks: [
          paragraph(
            `The current version of this policy is always published at this address and carries its effective date, which is ${LEGAL_EFFECTIVE_DATE}. When the school changes it materially it revises that date and records what changed, and continued use of the portal after that date is how the change is accepted.`,
          ),
          paragraph(
            "The school does not send an email or show a notice in the portal when this policy changes. Anyone who wants to know whether it has changed should check the date at the top of this page.",
          ),
        ],
      },
      {
        id: "contact",
        heading: "Contact",
        blocks: [
          paragraph(
            `Questions about this document, or about the information ${institutionName} holds about you:`,
          ),
          ...contactBlocks(facts),
        ],
      },
    ],
  };
}

export function termsOfServiceDocument(facts: LegalTenantFacts): LegalDocument {
  const { institutionName } = facts;

  return {
    title: "Terms of Service",
    lede: `The terms on which ${institutionName} provides access to its academic portal.`,
    sections: [
      {
        id: "status",
        heading: "Status of this document",
        blocks: [
          paragraph(
            "These terms describe how the portal actually works, and the school has adopted them. They have not yet been reviewed by a qualified legal adviser, and that review may change them.",
          ),
          OPEN_OBLIGATIONS_BLOCK,
          paragraph(
            `These terms take effect on ${LEGAL_EFFECTIVE_DATE}.`,
          ),
          paragraph(
            facts.operatorEntityName
              ? `${institutionName} is operated by ${facts.operatorEntityName}, which is the party these terms are with.`
              : `${institutionName} is its own legal entity and is the party these terms are with.`,
          ),
          pending(
            `the registered postal address of the entity responsible for ${institutionName}`,
          ),
        ],
      },
      {
        id: "who-may-use",
        heading: "Who may use the portal",
        blocks: [
          paragraph(
            "The portal is for people the school has admitted or appointed: students, instructors, and administrators. Access is issued by the school after approval. There is no public account registration, and nobody can create an account for themselves.",
          ),
          paragraph(
            "An account belongs to exactly one school. Signing in gives access to that school's records only.",
          ),
          ...(facts.collectsApplicationsHere
            ? [
                paragraph(
                  "The public application form is open to anyone, creates no account, and collects no payment.",
                ),
              ]
            : []),
        ],
      },
      {
        id: "your-account",
        heading: "Your account",
        blocks: [
          list(
            "An account is personal. Do not share your password or let anyone else use your account.",
            "An activation link from an invitation works once and expires. Ask the school for a new one rather than passing yours on.",
            "Tell the school promptly if you think someone else has used your account.",
            "Sign out on a shared device. Your session stays in that browser until you do.",
          ),
        ],
      },
      {
        id: "acceptable-use",
        heading: "Acceptable use",
        blocks: [
          list(
            "Use the portal for your own study, teaching, or administration at this school.",
            "Do not try to reach records that are not yours, another school's records, or anyone else's account.",
            "Do not republish another person's information you can see in the portal - a roster, an announcement naming someone, a recording - outside the school.",
            "Do not upload material you have no right to share, and do not upload medical, financial, or government identity documents; the portal is not built to hold them.",
            "Do not attempt to disrupt, probe, or overload the service.",
          ),
        ],
      },
      {
        id: "academic-records",
        heading: "Academic records and results",
        blocks: [
          paragraph(
            "The school is the owner of your academic record. Results shown while a term is running are working and projected values: an instructor submits them and an administrator can override them. They are not a certified academic outcome.",
          ),
          paragraph(
            "The explicit certification step that produces final academic history is not yet built. Until it is, treat any result you see in the portal as provisional, and ask the school for an official record.",
          ),
          paragraph(
            "Attendance, grades, and outcomes are recorded by instructors and administrators. If you believe one is wrong, raise it with the school; the portal does not let a student change it.",
          ),
        ],
      },
      {
        id: "your-work",
        heading: "Work you submit",
        blocks: [
          paragraph(
            "Work you submit stays yours. The school needs to keep, read, and assess it in order to teach the course and to record the outcome, and it keeps the submitted file and its assessment as part of the course record.",
          ),
          paragraph(
            "You keep ownership of your work. You give the school permission to store it, read it, show it to the people teaching and administering your course, and keep it as part of the course record. That permission is limited to running the course, assessing it, and maintaining your academic record, and the school takes nothing beyond it.",
          ),
          paragraph(
            "The school will not use your work for promotion, publication, or any other purpose outside teaching and assessment without asking you first and getting your agreement in writing.",
          ),
          paragraph(
            "Where a session is recorded, the recording is not held in this portal. An instructor notes where it is stored, and the portal releases that link only to a student whose own attendance for that session is marked excused.",
          ),
          pending(
            "whether the school records sessions as a matter of course, how long a recording is kept, and how a participant objects to being recorded; the portal holds no recordings today, so this becomes answerable when it does",
          ),
        ],
      },
      {
        id: "availability",
        heading: "Availability and changes to the service",
        blocks: [
          paragraph(
            "The school may change, suspend, or withdraw parts of the portal, including during maintenance. The portal is not guaranteed to be available at any particular moment, and the school will not always be able to give notice.",
          ),
          paragraph(
            "Where the portal links out to another service - an admissions site, a meeting room, a document in someone's Google Drive - that service has its own terms, and these terms do not cover it.",
          ),
        ],
      },
      {
        id: "suspension",
        heading: "Suspension and removal of access",
        blocks: [
          paragraph(
            "The school can suspend or remove access, for example when a person leaves, when an account is compromised, or when this section's rules are broken. Removing access does not erase academic history; the school keeps the record it is obliged to keep.",
          ),
        ],
      },
      {
        id: "warranty-and-liability",
        heading: "Warranty and liability",
        blocks: [
          paragraph(
            "The portal is provided as it is. The school does not promise that it will be available without interruption, that it will be free of errors, or that it will meet any particular requirement beyond what these terms describe.",
          ),
          paragraph(
            "The school's total liability to you, for everything arising out of these terms taken together, is limited to the fees you paid it in the twelve months before the claim, or one hundred dollars where you paid none. The school is not liable for indirect, incidental, or consequential loss, or for lost data, however it arises.",
          ),
          paragraph(
            "Nothing here limits a liability that the law does not permit to be limited.",
          ),
        ],
      },
      {
        id: "governing-law",
        heading: "Governing law and disputes",
        blocks: [
          paragraph(
            "These terms are governed by the law of the State of Michigan, and any dispute belongs to the courts of the county in which the responsible entity is registered.",
          ),
          paragraph(
            "Before either of us files anything, we each agree to raise the matter in writing and to spend thirty days trying to resolve it directly. Almost everything a school and a student disagree about is settled in that thirty days, and neither of us is giving up any right by trying.",
          ),
        ],
      },
      {
        id: "fees",
        heading: "Tuition and fees",
        blocks: [
          paragraph(
            "These terms do not set tuition, fees, or refunds. Those are published separately by the school and are not part of this agreement.",
          ),
          paragraph(
            "Access to the portal follows your enrollment or appointment, not your payment status. The portal holds no billing information and processes no payment, and it does not close an account because a balance is outstanding.",
          ),
        ],
      },
      {
        id: "school-policies",
        heading: "The school's own policies",
        blocks: [
          paragraph(
            "Alongside these terms you are bound by the school's academic integrity policy, its attendance policy, and its student code of conduct. Each is maintained by the school as its own document rather than restated here, so that it can be revised on its own terms.",
          ),
          pending(
            "where each of the three policies named above is published, so that these terms can link to it rather than only name it",
          ),
        ],
      },
      {
        id: "changes",
        heading: "Changes to these terms",
        blocks: [
          paragraph(
            `The current version of these terms is always published at this address and carries its effective date, which is ${LEGAL_EFFECTIVE_DATE}. When the school changes them materially it revises that date and records what changed, and continued use of the portal after that date is how the change is accepted.`,
          ),
          paragraph(
            "The school does not send an email or show a notice in the portal when these terms change. Anyone who wants to know whether they have changed should check the date at the top of this page.",
          ),
        ],
      },
      {
        id: "contact",
        heading: "Contact",
        blocks: [
          paragraph(`Questions about these terms:`),
          ...contactBlocks(facts),
        ],
      },
    ],
  };
}
